'use client';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLogout } from '@/hooks/useLogout';
import { useNotifications } from '@/hooks/useNotifications';
import { errorHandler } from '@/lib/errorHandler';
import { cn } from '@/lib/utils';
import { useAuthStore, type User } from '@/store/auth';
import { Bell, ChevronDown } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { SearchNavbar } from './search-navbar';

export function NavbarAuth({
  isSearchOpen,
  setIsSearchOpen,
}: {
  isSearchOpen: boolean;
  setIsSearchOpen: (open: boolean) => void;
}) {
  const user = useAuthStore((state) => state.user);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Fixed container with consistent width to prevent layout shift
  return (
    <div className="flex items-center gap-3 h-10 min-w-[180px] w-full justify-end">
      <SearchNavbar
        isSearchOpen={isSearchOpen}
        setIsSearchOpen={setIsSearchOpen}
      />
      <div className="flex items-center h-10 min-w-[120px] justify-end">
        {isLoaded ? (
          user ? (
            <AuthenticatedUI user={user} />
          ) : (
            <UnauthenticatedUI />
          )
        ) : (
          <PlaceholderUI />
        )}
      </div>
    </div>
  );
}

// Add this new component to show while authentication state is loading
function PlaceholderUI() {
  return (
    <div className="h-10 w-[120px] bg-ihs-blue/10 animate-pulse rounded-md"></div>
  );
}

function AuthenticatedUI({ user }: { user: User }) {
  const { mutate: logoutApi, isPending: isLoading } = useLogout();
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const { getUnreadCountData } = useNotifications({});
  const unreadCount = getUnreadCountData();

  const router = useRouter();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const onHandleLogout = () => {
    logoutApi(undefined, {
      onSuccess: () => {
        clearAuth();
      },
      onError: (error) => {
        toast.error(errorHandler(error));
      },
    });
  };

  const onHandleAccountSettings = () => {
    router.push('/account-settings');
  };

  const onHandleActivity = () => {
    router.push('/activity');
  };

  const isNotification = usePathname().includes('notification');

  return (
    <>
      <Link
        href="/notification"
        aria-label="Notification"
        className={cn(
          buttonVariants({ variant: 'ghost', size: 'icon' }),
          'text-white hover:bg-ihs-blue/50 border border-ihs-blue/50 mr-2 relative',
          isNotification && 'bg-white text-ihs-blue hover:bg-ihs-accent-blue'
        )}
      >
        <Bell
          className={cn(
            'h-5 w-5',
            isNotification && 'text-ihs-blue fill-ihs-blue'
          )}
        />
        {!!unreadCount && unreadCount > 0 && (
          <span className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-1 h-1 flex items-center justify-center"></span>
        )}
      </Link>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="text-white hover:bg-ihs-blue/50 border border-ihs-blue/50 h-10"
          >
            <Image
              src={user?.profilePicture ?? '/profile.svg'}
              width={24}
              height={24}
              alt="Profile icon"
              className="rounded-full w-6 h-6 mr-2 object-cover"
              priority
            />

            <span className="truncate max-w-[100px]">
              {user.fullName ??
                user.userName ??
                user.email?.split('@')[0] ??
                'User'}
            </span>

            <ChevronDown className="ml-1 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-40">
          <DropdownMenuItem onClick={onHandleActivity}>
            My Activity
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onHandleAccountSettings}>
            Account Settings
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowLogoutDialog(true)}>
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent className="max-w-md rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-bold mb-2">
              Log out?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base mb-4">
              Are you sure you want to log out?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col gap-2">
            <AlertDialogAction
              className={cn(
                buttonVariants({ variant: 'default', size: 'lg' }),
                'w-full rounded-full font-bold text-lg bg-ihs-blue text-white hover:bg-ihs-blue/90 uppercase tracking-wide',
                isLoading && 'opacity-70 pointer-events-none'
              )}
              onClick={onHandleLogout}
              disabled={isLoading}
            >
              LOGOUT
            </AlertDialogAction>
            <AlertDialogCancel
              className={cn(
                'absolute top-4 right-4 p-2 rounded-full hover:bg-black/10',
                'border-none bg-transparent text-black text-xl font-bold shadow-none'
              )}
              aria-label="Close"
            >
              ×
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function UnauthenticatedUI() {
  return (
    <>
      <Link
        href="/sign-up"
        className={cn(
          buttonVariants({ variant: 'default' }),
          'font-bold uppercase h-10'
        )}
      >
        Sign Up{' '}
      </Link>
      <Link
        href="/sign-in"
        className={cn(
          buttonVariants({ variant: 'outline' }),
          'font-bold uppercase h-10 bg-white ml-2 hover:bg-white/90'
        )}
      >
        Log In
      </Link>
    </>
  );
}
